import { PluginKey } from 'prosemirror-state';
import type { Transaction } from 'prosemirror-state';

import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type {
	NextEditorPlugin,
	NextEditorPluginFunctionOptionalConfigDefinition,
	OptionalPlugin,
} from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';

import { eventHandlersForLiveSearchExtension } from './eventHandlersForLiveSearchExtension.ts';
import { ensureUniqueAnchorNamesOnPasteAction } from './ensureUniqueAnchorNamesOnPasteAction.ts';
import { eventHandlersForCompanyHubSearchExtension } from './eventHandlersForCompanyHubSearchExtension.ts';

export const pluginName = 'extensionManager';
export const pluginKey: PluginKey<ExtensionManagerPluginState> =
	new PluginKey<ExtensionManagerPluginState>(pluginName);

type PageContext = {
	isLivePage?: boolean;
	spaceKey?: string;
	contentId?: string;
};

type AnchorMacroToolbarState = {
	showAnchorLinkCopiedTooltip?: boolean;
	pageContext?: PageContext;
};

type ExtensionManagerPluginState = AnchorMacroToolbarState | undefined;

export type ExtensionManagerPlugin = NextEditorPlugin<
	'extensionManager',
	{
		sharedState: ExtensionManagerPluginState;
		dependencies: [OptionalPlugin<AnalyticsPlugin>];
	}
>;

export const createPlugin = (): SafePlugin<
	NextEditorPluginFunctionOptionalConfigDefinition<
		'extensionManager',
		{
			sharedState: ExtensionManagerPluginState;
			dependencies: [OptionalPlugin<AnalyticsPlugin>];
		},
		undefined
	>
> =>
	new SafePlugin<ExtensionManagerPlugin>({
		key: pluginKey,
		state: {
			init: (): ExtensionManagerPluginState => ({}),
			apply(tr: Transaction, pluginState: ExtensionManagerPluginState) {
				const metadata = tr.getMeta(pluginKey);

				if (metadata) {
					return {
						...pluginState,
						...metadata,
					};
				}

				return pluginState;
			},
		},
		props: {
			handleDOMEvents: {
				...eventHandlersForLiveSearchExtension,
				...eventHandlersForCompanyHubSearchExtension,
			},
			transformPasted: ensureUniqueAnchorNamesOnPasteAction,
		},
	});
